// ─────────────────────────────────────────────
//  src/pages/Alerts.js  —  Phase 5
//  Alert Manager:
//  • Create TARGET / STOP-LOSS price alerts
//  • Live CMP vs trigger progress bar per alert
//  • Active / Triggered tabs
//  • Dismiss + delete alerts
//  • Auto-check triggers against live prices
// ─────────────────────────────────────────────
import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAlerts }   from "../hooks/useAlerts";
import { fetchBatchQuotes } from "../config/api";
import "./Alerts.css";

const fmtINR = (n) =>
  n != null ? "₹" + Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }) : "—";

// ── Create Alert Modal ────────────────────────
function CreateAlertModal({ onSave, onClose }) {
  const [form, setForm] = useState({
    symbol: "", exchange: "NSE",
    alert_type: "TARGET",
    trigger_price: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [err,    setErr]    = useState("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSave() {
    const sym = form.symbol.trim().toUpperCase();
    const prc = parseFloat(form.trigger_price);
    if (!sym) return setErr("Symbol is required.");
    if (!prc || prc <= 0) return setErr("Trigger price must be > 0.");
    setSaving(true);
    try {
      await onSave({ ...form, symbol: sym, trigger_price: prc });
      onClose();
    } catch (e) { setErr(e.message); }
    finally { setSaving(false); }
  }

  return (
    <div className="al-modal-overlay" onClick={onClose}>
      <div className="al-modal" onClick={e => e.stopPropagation()}>
        <div className="al-modal-header">
          <h3>🔔 Create Price Alert</h3>
          <button className="al-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="al-modal-body">

          {/* Type toggle */}
          <div className="al-field">
            <label>Alert Type</label>
            <div className="al-type-toggle">
              <button
                className={`al-type-opt target ${form.alert_type === "TARGET" ? "active" : ""}`}
                onClick={() => set("alert_type", "TARGET")}
              >
                🎯 Target Price
              </button>
              <button
                className={`al-type-opt stoploss ${form.alert_type === "STOPLOSS" ? "active" : ""}`}
                onClick={() => set("alert_type", "STOPLOSS")}
              >
                🛑 Stop Loss
              </button>
            </div>
          </div>

          {/* Symbol + Exchange */}
          <div className="al-field-row">
            <div className="al-field">
              <label>Symbol *</label>
              <input
                placeholder="e.g. RELIANCE"
                value={form.symbol}
                onChange={e => set("symbol", e.target.value.toUpperCase())}
              />
            </div>
            <div className="al-field">
              <label>Exchange</label>
              <select value={form.exchange} onChange={e => set("exchange", e.target.value)}>
                <option value="NSE">NSE</option>
                <option value="BSE">BSE</option>
              </select>
            </div>
          </div>

          {/* Trigger price */}
          <div className="al-field">
            <label>
              {form.alert_type === "TARGET" ? "Target Price (₹) — Alert when CMP reaches this" : "Stop Loss (₹) — Alert when CMP falls to this"}
            </label>
            <input
              type="number" min="0" step="0.01"
              placeholder={form.alert_type === "TARGET" ? "e.g. 3000" : "e.g. 2200"}
              value={form.trigger_price}
              onChange={e => set("trigger_price", e.target.value)}
            />
          </div>

          {/* Notes */}
          <div className="al-field">
            <label>Notes (optional)</label>
            <input
              placeholder="e.g. Breakout level, quarterly results…"
              value={form.notes}
              onChange={e => set("notes", e.target.value)}
            />
          </div>

          {err && <div style={{ color: "#ef4444", fontSize: "0.8rem" }}>⚠ {err}</div>}
        </div>
        <div className="al-modal-footer">
          <button className="al-btn al-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="al-btn al-btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Creating…" : "Create Alert"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Alert Card ────────────────────────────────
function AlertCard({ alert, cmp, onDismiss, onDelete, onClick }) {
  const isTarget   = alert.alert_type === "TARGET";
  const isTriggered = alert.status === "TRIGGERED";
  const trigger    = parseFloat(alert.trigger_price) || 0;

  // Progress: how far CMP has moved toward trigger (0–100%)
  let progress = 0;
  let distLabel = "";
  if (cmp && trigger) {
    if (isTarget) {
      // going up: progress = cmp/trigger
      progress  = Math.min((cmp / trigger) * 100, 100);
      const pct = trigger > 0 ? (((trigger - cmp) / cmp) * 100).toFixed(1) : 0;
      distLabel = cmp >= trigger ? "✓ Target reached!" : `${pct}% away`;
    } else {
      // stop loss: going down, progress = trigger/cmp inverted
      progress  = Math.min((trigger / cmp) * 100, 100);
      const pct = cmp > 0 ? (((cmp - trigger) / cmp) * 100).toFixed(1) : 0;
      distLabel = cmp <= trigger ? "⚠ Stop loss hit!" : `${pct}% buffer`;
    }
  }

  const accentColor = isTarget ? "#22c55e" : "#ef4444";

  return (
    <div
      className={`al-card ${isTarget ? "target" : "stoploss"} ${isTriggered ? "triggered" : ""}`}
      onClick={onClick}
      style={{ cursor: "pointer" }}
    >
      <div className="al-card-top">
        <div>
          <span className="al-card-sym">{alert.symbol}</span>
          <span className="al-card-exch">{alert.exchange || "NSE"}</span>
        </div>
        <div className="al-card-actions" onClick={e => e.stopPropagation()}>
          {!isTriggered && (
            <button className="al-card-btn" title="Dismiss" onClick={onDismiss}>✓</button>
          )}
          <button className="al-card-btn del" title="Delete" onClick={onDelete}>🗑</button>
        </div>
      </div>

      <div className="al-card-body">
        <div className="al-type-row">
          <span className={`al-type-badge ${isTarget ? "target" : "stoploss"}`}>
            {isTarget ? "🎯 Target" : "🛑 Stop Loss"}
          </span>
          {isTriggered && <span className="al-triggered-badge">⚡ Triggered</span>}
        </div>

        <div className="al-price-row">
          <span className="al-trigger-label">Trigger</span>
          <span className="al-trigger-price">{fmtINR(trigger)}</span>
        </div>

        {cmp > 0 && (
          <>
            <div className="al-cmp-row">
              <span className="al-cmp-label">CMP</span>
              <span className="al-cmp-val">{fmtINR(cmp)}</span>
            </div>
            <div className="al-progress-wrap">
              <div className="al-progress-label">
                <span>{isTarget ? "Progress to target" : "Proximity to stop"}</span>
                <span style={{ color: progress >= 95 ? accentColor : "#475569" }}>{distLabel}</span>
              </div>
              <div className="al-progress-bar">
                <div
                  className="al-progress-fill"
                  style={{ width: `${progress}%`, background: accentColor }}
                />
              </div>
            </div>
          </>
        )}

        {alert.notes && <div className="al-card-notes">"{alert.notes}"</div>}
      </div>
    </div>
  );
}

// ── Main Alerts Page ──────────────────────────
export default function Alerts() {
  const navigate = useNavigate();
  const { alerts, loading, createAlert, dismissAlert, deleteAlert } = useAlerts();
  const [showModal, setShowModal]   = useState(false);
  const [liveData,  setLiveData]    = useState({});
  const [activeTab, setActiveTab]   = useState("active");

  // ── Fetch live prices for all alerted symbols ──
  useEffect(() => {
    if (!alerts.length) return;
    const unique = [...new Set(alerts.map(a => a.symbol))];
    fetchBatchQuotes(unique.map(s => ({ symbol: s, exchange: "NSE" })))
      .then(quotes => {
        const map = {};
        quotes.forEach(q => { if (q && !q.error) map[q.symbol] = q.price; });
        setLiveData(map);
      })
      .catch(console.error);
    const iv = setInterval(() => {
      fetchBatchQuotes(unique.map(s => ({ symbol: s, exchange: "NSE" })))
        .then(quotes => {
          const map = {};
          quotes.forEach(q => { if (q && !q.error) map[q.symbol] = q.price; });
          setLiveData(map);
        }).catch(console.error);
    }, 3 * 60 * 1000);
    return () => clearInterval(iv);
  }, [alerts]);

  // ── Auto-trigger detection ─────────────────
  useEffect(() => {
    alerts.forEach(alert => {
      if (alert.status !== "ACTIVE") return;
      const cmp     = liveData[alert.symbol];
      const trigger = parseFloat(alert.trigger_price);
      if (!cmp || !trigger) return;
      const hit = alert.alert_type === "TARGET"
        ? cmp >= trigger
        : cmp <= trigger;
      if (hit) dismissAlert(alert.id); // marks as TRIGGERED in real hook
    });
  }, [liveData, alerts, dismissAlert]);

  const activeAlerts    = alerts.filter(a => a.status === "ACTIVE");
  const triggeredAlerts = alerts.filter(a => a.status !== "ACTIVE");
  const displayed       = activeTab === "active" ? activeAlerts : triggeredAlerts;

  const targetCount   = activeAlerts.filter(a => a.alert_type === "TARGET").length;
  const stopLossCount = activeAlerts.filter(a => a.alert_type === "STOPLOSS").length;

  if (loading) {
    return (
      <div className="al-page">
        {[1,2,3].map(i => <div key={i} className="al-skel-row" />)}
      </div>
    );
  }

  return (
    <div className="al-page">

      {/* ── Header ── */}
      <div className="al-header">
        <div className="al-header-left">
          <h1>🔔 Alerts</h1>
          <p>{activeAlerts.length} active alert{activeAlerts.length !== 1 ? "s" : ""} · auto-checks every 3 min</p>
        </div>
        <div className="al-header-actions">
          <button className="al-btn al-btn-primary" onClick={() => setShowModal(true)}>
            ＋ New Alert
          </button>
        </div>
      </div>

      {/* ── Summary bar ── */}
      {alerts.length > 0 && (
        <div className="al-summary-bar">
          <div className="al-stat">
            <span className="al-stat-label">Active</span>
            <span className="al-stat-val">{activeAlerts.length}</span>
          </div>
          <div className="al-stat">
            <span className="al-stat-label">Target Alerts</span>
            <span className="al-stat-val target">{targetCount}</span>
          </div>
          <div className="al-stat">
            <span className="al-stat-label">Stop Loss</span>
            <span className="al-stat-val stoploss">{stopLossCount}</span>
          </div>
          <div className="al-stat">
            <span className="al-stat-label">Triggered</span>
            <span className="al-stat-val triggered">{triggeredAlerts.length}</span>
          </div>
        </div>
      )}

      {/* ── Panel ── */}
      <div className="al-panel">
        <div className="al-panel-header">
          <div className="al-tabs">
            <button
              className={`al-tab ${activeTab === "active" ? "active" : ""}`}
              onClick={() => setActiveTab("active")}
            >
              Active ({activeAlerts.length})
            </button>
            <button
              className={`al-tab ${activeTab === "triggered" ? "active" : ""}`}
              onClick={() => setActiveTab("triggered")}
            >
              Triggered ({triggeredAlerts.length})
            </button>
          </div>
        </div>

        {/* Empty */}
        {!alerts.length ? (
          <div className="al-empty">
            <div className="al-empty-icon">🔔</div>
            <p className="al-empty-title">No alerts yet</p>
            <p className="al-empty-sub">
              Set target price alerts to know when to buy, and stop-loss alerts
              to protect your portfolio. Live prices checked every 3 minutes.
            </p>
            <button className="al-btn al-btn-primary" style={{ marginTop: 8 }} onClick={() => setShowModal(true)}>
              ＋ Create First Alert
            </button>
          </div>
        ) : !displayed.length ? (
          <div className="al-empty">
            <div className="al-empty-icon">{activeTab === "active" ? "✅" : "📭"}</div>
            <p className="al-empty-title">
              {activeTab === "active" ? "All alerts triggered!" : "No triggered alerts yet"}
            </p>
          </div>
        ) : (
          <div className="al-grid">
            {displayed.map(alert => (
              <AlertCard
                key={alert.id}
                alert={alert}
                cmp={liveData[alert.symbol] || 0}
                onDismiss={() => dismissAlert(alert.id)}
                onDelete={() => window.confirm(`Delete alert for ${alert.symbol}?`) && deleteAlert(alert.id)}
                onClick={() => navigate(`/stock/${alert.symbol}`)}
              />
            ))}
          </div>
        )}
      </div>

      {showModal && (
        <CreateAlertModal
          onSave={createAlert}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}